import { getTimezones } from 'features/sendouts/components/forms/InterviewSchedule/components/TimezoneSelector/TimezoneSelector.utils';
import { TIME_FRAME_MAP } from 'features/sendouts/components/forms/InterviewSchedule/InterviewSchedule.constants';
import { INTERVIEW_REMINDERS_STATUS } from 'features/sendouts/constants/constants';
import sumBy from 'lodash/sumBy';
import moment from 'moment-timezone';
import strings from 'strings';
import { DateFormats } from 'UI/constants/defaults';
import { formatBytes } from 'UI/utils';

const MAX_ATTACHMENTS_SIZE_MB =
  parseInt(
    window.GPAC_ENV?.SENDOUTS_MAX_ATTACHMENTS_SIZE_MB ??
      process.env.REACT_APP_SENDOUTS_MAX_ATTACHMENTS_SIZE_MB ??
      '20',
    10
  ) || 20;

export const DEFAULT_MAX_ATTACHMENTS_SIZE = MAX_ATTACHMENTS_SIZE_MB * 1024 * 1024; // total across all attachment categories

export const getAttachmentsSizeValidationError = (attachments = [], maxAttachmentsSize) => {
  if (!maxAttachmentsSize) return null;

  const totalSize = sumBy(attachments, 'size');
  if (totalSize <= maxAttachmentsSize) return null;

  return strings.formatString(strings.sendoutsV2.form.sections.attachments.errors.maxSizeExceeded, {
    total: formatBytes(totalSize),
    max: formatBytes(maxAttachmentsSize)
  });
};

const NEUTRAL_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const buildInterviewMoment = ({ interviewDate, time, timezoneName }) => {
  if (!interviewDate || !time || !timezoneName) return null;

  const matchedTime = NEUTRAL_TIME_PATTERN.exec(time);

  if (!matchedTime) return null;

  const dateMoment = moment(interviewDate);

  if (!dateMoment.isValid()) return null;

  const [, hour, minute] = matchedTime;

  const interviewMoment = moment.tz(
    {
      year: dateMoment.year(),
      month: dateMoment.month(),
      date: dateMoment.date(),
      hour: Number(hour),
      minute: Number(minute),
      second: 0,
      millisecond: 0
    },
    timezoneName
  );

  return interviewMoment.format(DateFormats.SimpleTime24Format) === time ? interviewMoment : null;
};

export const buildInterviewPayload = (formData = {}) => {
  const interviewDate = formData?.interviewDate ?? null;
  const from = formData?.interviewFromHour ?? null;
  const timezone = formData?.interviewTimezone?.momentName ?? null;
  const interviewMoment = buildInterviewMoment({
    interviewDate,
    time: from,
    timezoneName: timezone
  });
  const interviewToMoment = buildInterviewMoment({
    interviewDate,
    time: formData?.interviewToHour,
    timezoneName: timezone
  });

  return {
    interview_type_id: formData?.interviewType ?? null,
    date: interviewDate,
    timeFrame: formData?.interviewTimeFrame?.id ?? null,
    startTimestamp: interviewMoment ? interviewMoment.toISOString() : null,
    endTimestamp: interviewToMoment ? interviewToMoment.toISOString() : null,
    timezone,
    offset: interviewMoment ? interviewMoment.utcOffset() / 60 : null,
    interviewAddress: formData?.interviewAddress ?? null,
    interviewLink: formData?.interviewLink ?? null,
    sendReminders: !formData?.dontSendReminders
  };
};

const toInterviewMoment = (timestamp, timezone) =>
  timezone ? moment.utc(timestamp).tz(timezone) : moment.utc(timestamp);

const buildInterviewDate = ({ timestamp, timezone }) => {
  if (!timestamp) return null;

  const interviewMoment = toInterviewMoment(timestamp, timezone);

  return interviewMoment.isValid()
    ? new Date(interviewMoment.year(), interviewMoment.month(), interviewMoment.date())
    : null;
};

const getInterviewTimeFrame = ({ endTimestamp, timeFrame }) => {
  const timeFrameFromResponse =
    TIME_FRAME_MAP[timeFrame] || Object.values(TIME_FRAME_MAP).find(({ id }) => id === timeFrame);

  return (
    timeFrameFromResponse || (endTimestamp ? TIME_FRAME_MAP.TimeRange : TIME_FRAME_MAP.FixedHour)
  );
};

export const buildInterviewFormValues = (interview = {}) => {
  const interviewDateTimestamp = interview.startTimestamp ?? interview.interviewDate;
  const interviewDate = buildInterviewDate({
    timestamp: interviewDateTimestamp,
    timezone: interview.interviewTimeZone
  });
  const interviewFromMoment = interview.startTimestamp
    ? toInterviewMoment(interview.startTimestamp, interview.interviewTimeZone)
    : null;
  const interviewToMoment = interview.endTimestamp
    ? toInterviewMoment(interview.endTimestamp, interview.interviewTimeZone)
    : null;
  const interviewFromHour = interviewFromMoment?.isValid()
    ? interviewFromMoment.format(DateFormats.SimpleTime24Format)
    : null;
  const interviewToHour = interviewToMoment?.isValid()
    ? interviewToMoment.format(DateFormats.SimpleTime24Format)
    : null;

  return {
    interviewType: interview.interviewTypeId ?? null,
    interviewDate,
    // TODO: derive from timeFrame directly once GET returns it; today it falls back to endTimestamp.
    interviewTimeFrame: getInterviewTimeFrame(interview),
    interviewFromHour,
    interviewToHour,
    interviewTimezone:
      getTimezones({ interviewDate, time: interviewFromHour }).find(
        timezone => timezone.momentName === interview.interviewTimeZone
      ) ?? null,
    interviewAddress: interview.address ?? null,
    interviewLink: interview.url ?? null,
    dontSendReminders: interview.reminders === INTERVIEW_REMINDERS_STATUS.Disabled
  };
};

export const mapCandidateAttachments = files =>
  (files || []).map(file => ({
    ...file,
    file_type_id: file.file_type_id ?? file.fileTypeId ?? file.fileType?.id,
    file_name: file.file_name ?? file.fileName,
    created_at: file.created_at ?? file.createdAt,
    updated_at: file.updated_at ?? file.updatedAt
  }));
